import { Zap, ShieldCheck, TrendingUp, Sparkles } from "lucide-react";
import { FeaturesBg } from "@/components/sections/ContactVisuals";

const features = [
  { id: "speed",  icon: Zap,           title: "Lightning Fast",      desc: "Sub-second loads, edge caching and image-perfect rendering on every device.",        metric: "99 / 100", metricLabel: "Lighthouse" },
  { id: "secure", icon: ShieldCheck,   title: "Secure Architecture", desc: "Hardened auth, encrypted secrets, audited dependencies and zero-trust networking.", metric: "SOC 2",   metricLabel: "Ready" },
  { id: "scale",  icon: TrendingUp,    title: "Scalable Systems",    desc: "Built for growth — autoscaling, multi-region failover and queue-based workloads.",  metric: "10M+",    metricLabel: "Req / day" },
  { id: "ux",     icon: Sparkles,      title: "Clean UI / UX",       desc: "Conversion-focused interfaces with motion, accessibility and pixel-level polish.",   metric: "WCAG AA", metricLabel: "Accessible" },
];

export default function Features() {
  return (
    <section id="features" data-testid="features-section" className="relative py-20 sm:py-28 bg-paper-soft overflow-hidden">
      <div className="absolute inset-0 bg-lattice opacity-50 pointer-events-none" />
      <div className="absolute inset-x-0 top-0 divider-glow" />
      <div className="absolute inset-x-0 bottom-0 divider-glow" />

      {/* Animated UI bg behind heading */}
      <FeaturesBg />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6">
        <div className="grid lg:grid-cols-12 gap-8 items-end mb-12">
          <div className="lg:col-span-7 reveal">
            <div className="text-xs font-mono-d uppercase tracking-[0.28em] text-[#10b981] mb-3">
              / 03 — Why ARJ
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[0.98] tracking-tight text-[#0a0e1a]">
              Engineered for speed.
              <br />
              <span className="text-grad">Built for trust.</span>
            </h2>
          </div>
          <div className="lg:col-span-5 reveal" style={{ transitionDelay: "100ms" }}>
            <p className="text-[#475569] text-base sm:text-lg">
              Every product we ship is benchmarked on four pillars — performance, security, scale, and craft.
            </p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
          {features.map((f, i) => {
            const Icon = f.icon;
            return (
              <div
                key={f.id}
                data-testid={`feature-card-${f.id}`}
                className="reveal surface-strong card-lift rounded-2xl p-6"
                style={{ transitionDelay: `${i * 70}ms` }}
              >
                <div className="flex items-center justify-between mb-5">
                  <span className="grid place-items-center w-11 h-11 rounded-xl bg-[#f4f7ff] border border-[#e6ecff]">
                    <Icon className="w-5 h-5 text-[#0066ff]" strokeWidth={2.2} />
                  </span>
                  <div className="text-right">
                    <div className="font-display text-base font-bold text-[#0a0e1a]">{f.metric}</div>
                    <div className="text-[10px] uppercase tracking-wider text-[#6b7280] font-mono-d">{f.metricLabel}</div>
                  </div>
                </div>
                <h3 className="font-display text-lg font-semibold text-[#0a0e1a] mb-2">{f.title}</h3>
                <p className="text-sm text-[#475569] leading-relaxed">{f.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
